import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import Navbar from './components/Navbar';
import AnnouncementBanner from './components/AnnouncementBanner';
import Home from './pages/Home';
import TarotReading from './pages/TarotReading';
import About from './pages/About';
import Contact from './pages/Contact';
import Booking from './pages/Booking';
import { Profile } from './pages/Profile';
import AdminLogin from './pages/AdminLogin';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { authService } from './services/authService';
import { User } from 'firebase/auth';
import AdminLayout from './layouts/AdminLayout';
import { adminRoutes } from './routes/adminRoutes';
import { ForgotPassword } from './pages/ForgotPassword';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

// React Router future flags
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff9a9e',
    },
    secondary: {
      main: '#fad0c4',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#1a1a2e',
      paper: '#16213e',
    },
  },
  typography: {
    fontFamily: "'Segoe UI', 'Roboto', 'Oxygen', sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Admin Route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async (user: User | null) => {
      if (!user) {
        setIsAdminUser(false);
        setLoading(false);
        return;
      }

      const isAdmin = await authService.isAdmin(user);
      setIsAdminUser(isAdmin);
      setLoading(false);

      if (!isAdmin) {
        window.location.href = '/admin/login';
      }
    };

    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user);
      checkAdminStatus(user);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  if (!user || !isAdminUser) {
    return <Navigate to="/admin/login" />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
          <CssBaseline />
          <Router {...router}>
            <Routes>
              {/* Admin routes */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                {adminRoutes[0].children?.map((route) => (
                  <Route
                    key={route.path || 'index'}
                    path={route.path}
                    index={!route.path}
                    element={route.element}
                  />
                ))}
              </Route>

              {/* Public routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/*"
                element={
                  <>
                    <Navbar />
                    <AnnouncementBanner />
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/tarot-reading" element={<TarotReading />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/booking" element={
                        <ProtectedRoute>
                          <Booking />
                        </ProtectedRoute>
                      } />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } />
                      <Route path="/login" element={<LoginForm />} />
                      <Route path="/register" element={<RegisterForm />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/randevu" element={
                        <ProtectedRoute>
                          <Booking />
                        </ProtectedRoute>
                      } />
                    </Routes>
                  </>
                }
              />
            </Routes>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
